import React from 'react';
import { SearchIcon } from 'glints-aries';
import { omitBy } from 'lodash-es';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';

import { getUtmParams } from 'src/common/links';
import { ROUTES } from 'src/common/routes';
import { StringCaseType, StringUtils } from 'src/common/utils/stringUtils';
import { GoogleTagManager } from 'src/components/GoogleTagManager';
import {
  SearchRouterQuery,
  SearchRouterQueryKey,
} from 'src/modules/Opportunities/pages/Explore/types';

import { maxSearchSuggestions } from './constants';
import * as S from './styles.sc';

interface Props {
  pastSearches: SearchRouterQuery[];
  popularSearches: Array<{ keyword: string; type: string }>;
}

type SearchResult = {
  keyword: string;
  type: string;
  locationName?: string;
} & Partial<SearchRouterQuery>;

export const SearchSuggestions: React.FC<React.PropsWithChildren<Props>> = ({
  pastSearches,
  popularSearches,
}) => {
  const router = useRouter();
  const marketingURLParams = getUtmParams(router.query);
  const intl = useIntl();

  const processedPastSearches = pastSearches
    .filter((search) => {
      const keyword = search[SearchRouterQueryKey.Keyword];
      return typeof keyword === 'string' && keyword.trim().length > 0;
    })
    .map((search) => {
      const keyword = search[SearchRouterQueryKey.Keyword] as string;
      return {
        ...search,
        keyword: keyword.trim(),
        locationName: search[SearchRouterQueryKey.Location],
        type: intl.formatMessage({
          id: 'chip-text_recent',
          defaultMessage: 'Recent:',
        }),
      };
    });

  const processedPopularSearches = popularSearches.map((search) => ({
    ...search,
    type: intl.formatMessage({
      id: 'chip-text_popular',
      defaultMessage: 'Popular:',
    }),
  }));

  const mergedSearchResults: SearchResult[] = [
    ...processedPastSearches,
    ...processedPopularSearches,
  ].slice(0, maxSearchSuggestions);

  const onPastSearchItemClick = (searchCondition: SearchResult) => {
    const query = omitBy(
      {
        [SearchRouterQueryKey.Keyword]: searchCondition.keyword,
        [SearchRouterQueryKey.Country]:
          searchCondition[SearchRouterQueryKey.Country],
        [SearchRouterQueryKey.Location]:
          searchCondition[SearchRouterQueryKey.Location],
        [SearchRouterQueryKey.LocationId]:
          searchCondition[SearchRouterQueryKey.LocationId],
        [SearchRouterQueryKey.LowestLocationlevel]:
          searchCondition[SearchRouterQueryKey.LowestLocationlevel],
        ...marketingURLParams,
      },
      (value) => !value
    ) as Record<string, string>;

    router.push({
      pathname: `/${ROUTES.opportunitiesJobsExplore}`,
      query,
    });
  };

  if (!mergedSearchResults.length) return null;

  return (
    <S.Container role="region" aria-label="Search suggestions list">
      {mergedSearchResults.map((searchResult, index) => {
        const label = searchResult.locationName
          ? intl.formatMessage(
              {
                id: 'searchSuggestion.with.location',
                defaultMessage: '{keyword} in {locationName}',
              },
              {
                keyword: StringUtils.convertOtherCaseToTitleCase(
                  searchResult.keyword,
                  StringCaseType.NormalText
                ),
                locationName: searchResult.locationName,
              }
            )
          : searchResult.keyword;

        const accessibleName = `${searchResult.type}${label}`;

        return (
          <S.ItemWrapper key={index} role="button" aria-label={accessibleName}>
            <GoogleTagManager tag="jobs_explore_tab-past_job_search_tag">
              <S.Tag onClick={() => onPastSearchItemClick(searchResult)}>
                <SearchIcon />
                <S.SearchTypeLabel>{searchResult.type}</S.SearchTypeLabel>
                <S.SearchKeywordLabel>{label}</S.SearchKeywordLabel>
              </S.Tag>
            </GoogleTagManager>
          </S.ItemWrapper>
        );
      })}
    </S.Container>
  );
};
